// Single-flight coordinator for /api/auth/refresh.
//
// The IDP rotates refresh tokens single-use: concurrent callers holding the
// same idp_rt race, and losers get 401 REFRESH_SESSION_NOT_FOUND. This module
// is the canonical transport + guard owner — every caller (axios 401
// interceptor, useRefresh, auth-boot) funnels through the same singleton so
// there is no per-caller transport divergence.
//
// Ownership (what this module owns, and no one else):
//   1. The inflight HTTP slot (one fetch per "flight" — late joiners get a
//      cloned Response).
//   2. The shared guard flag (isRefreshBlocked / blockRefresh /
//      resetRefreshBlock). Callers never mutate this directly.
//   3. Request construction for /api/auth/refresh — URL, headers, body —
//      driven from typed inputs, so cookie vs json delivery and CSRF
//      handling stay identical across callers.
//
// Failure-path race fix: the blocked flag is set INSIDE the critical section
// (via the onBeforeRelease hook) before the inflight slot clears. A trailing
// caller arriving just after slot release therefore observes the block and
// short-circuits instead of firing a second fetch.
const BLOCK_FLAG = '__TEE_TIME_REFRESH_BLOCKED__';
const TRANSIENT_BACKOFF_BASE_MS = 1_000;
const TRANSIENT_BACKOFF_MAX_MS = 30_000;
const DEFAULT_429_COOLDOWN_MS = 5_000;
let inflight = null;
let transientCooldown = null;
let transientFailureCount = 0;
function getBlockStore() {
    try {
        return globalThis;
    }
    catch {
        return {};
    }
}
/** Returns true when refresh is disabled until the user re-authenticates. */
export function isRefreshBlocked() {
    return Boolean(getBlockStore()[BLOCK_FLAG]);
}
/** Disable refresh until a successful re-auth clears the block. */
export function blockRefresh() {
    getBlockStore()[BLOCK_FLAG] = true;
}
/** Clear the block after a successful login / MFA / proactive refresh. */
export function resetRefreshBlock() {
    getBlockStore()[BLOCK_FLAG] = false;
    clearTransientCooldown();
}
/** Signaled when `singleFlightRefresh` is called while the guard is set. */
export class RefreshBlockedError extends Error {
    constructor(message = 'Refresh disabled until user re-authenticates.') {
        super(message);
        this.name = 'RefreshBlockedError';
    }
}
/**
 * Low-level single-flight primitive. Callers that need full control over
 * request construction may use this directly; prefer `singleFlightRefresh`
 * for the canonical transport contract.
 *
 * The executor MUST NOT consume the body (.json(), .text()) — the coordinator
 * clones the Response for every awaiter.
 */
export async function singleFlightRefreshFetch(doFetch, options) {
    if (inflight) {
        const shared = await inflight.response;
        return shared.clone();
    }
    const slot = {
        response: undefined,
        startedAt: Date.now(),
    };
    slot.response = (async () => {
        try {
            const res = await doFetch();
            if (options?.onBeforeRelease) {
                try {
                    options.onBeforeRelease(res);
                }
                catch {
                    // Guard-hook failure must not stop the response from reaching callers.
                }
            }
            return res;
        }
        finally {
            if (inflight === slot)
                inflight = null;
        }
    })();
    inflight = slot;
    const original = await slot.response;
    return original.clone();
}
/**
 * Canonical single-flight refresh call. Resolves the target URL, assembles
 * the shared header set (CSRF, audience, token-delivery), selects the body
 * shape from delivery mode, and delegates to the single-flight primitive.
 *
 * Returns a cloned `Response` for caller-local parsing. Callers must not
 * replicate transport concerns.
 *
 * - Short-circuits with `RefreshBlockedError` when `isRefreshBlocked()` is
 *   true at entry. No fetch fires and no slot is consumed.
 * - On HTTP 401 from the IDP the coordinator calls `blockRefresh()` inside
 *   the critical section, so concurrent or trailing callers observe the
 *   block deterministically.
 * - 5xx / 429 / thrown network errors are left as transient: the guard is
 *   NOT set, and callers classify transience themselves. HTTP transient
 *   responses open a shared cooldown so remounts, route guards, and
 *   visibility handlers cannot immediately start a refresh storm after the
 *   first 429/5xx response. 429 honours Retry-After when the IDP sends it.
 */
export async function singleFlightRefresh(inputs) {
    if (isRefreshBlocked()) {
        throw new RefreshBlockedError();
    }
    const cachedTransient = getUsableTransientCooldown();
    if (cachedTransient) {
        return cachedTransient.clone();
    }
    const target = buildRefreshUrl(inputs.basePath);
    const headers = buildRefreshHeaders(inputs);
    const body = buildRefreshBody(inputs);
    const fetchImpl = inputs.fetchImpl ?? globalThis.fetch;
    if (typeof fetchImpl !== 'function') {
        throw new Error('No fetch implementation available for singleFlightRefresh.');
    }
    return singleFlightRefreshFetch(() => fetchImpl(target, {
        method: 'POST',
        credentials: 'include',
        headers,
        cache: 'no-store',
        body: JSON.stringify(body),
    }), {
        onBeforeRelease: (res) => {
            if (res.status === 401) {
                clearTransientCooldown();
                blockRefresh();
                return;
            }
            if (isTransientRefreshStatus(res.status)) {
                storeTransientCooldown(res);
                return;
            }
            clearTransientCooldown();
        },
    });
}
function buildRefreshUrl(basePath) {
    const trimmed = (basePath ?? '').replace(/\/+$/, '');
    return trimmed ? `${trimmed}/api/auth/refresh` : '/api/auth/refresh';
}
function buildRefreshHeaders(inputs) {
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Client-Audience': inputs.audience,
    };
    if (inputs.xsrfToken)
        headers['X-XSRF-Token'] = inputs.xsrfToken;
    if (inputs.tokenDelivery === 'cookie')
        headers['X-Token-Delivery'] = 'cookie';
    return headers;
}
function buildRefreshBody(inputs) {
    if (inputs.tokenDelivery === 'cookie')
        return {};
    return inputs.refreshPayload ?? {};
}
function isTransientRefreshStatus(status) {
    return status === 429 || status >= 500;
}
function getUsableTransientCooldown() {
    if (!transientCooldown)
        return null;
    if (Date.now() < transientCooldown.expiresAt)
        return transientCooldown.response;
    transientCooldown = null;
    return null;
}
function storeTransientCooldown(res) {
    const retryAfterMs = res.status === 429 ? parseRetryAfterMs(res.headers.get('Retry-After')) : null;
    const exponentialMs = Math.min(TRANSIENT_BACKOFF_MAX_MS, TRANSIENT_BACKOFF_BASE_MS * 2 ** transientFailureCount);
    const cooldownMs = retryAfterMs ??
        (res.status === 429 ? Math.max(DEFAULT_429_COOLDOWN_MS, exponentialMs) : exponentialMs);
    transientFailureCount += 1;
    transientCooldown = {
        response: res.clone(),
        expiresAt: Date.now() + cooldownMs,
    };
}
function parseRetryAfterMs(value) {
    if (!value)
        return null;
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    const seconds = Number(trimmed);
    if (Number.isFinite(seconds) && seconds > 0) {
        return Math.min(TRANSIENT_BACKOFF_MAX_MS, seconds * 1_000);
    }
    const targetMs = Date.parse(trimmed);
    if (!Number.isFinite(targetMs))
        return null;
    const delayMs = targetMs - Date.now();
    if (delayMs <= 0)
        return null;
    return Math.min(TRANSIENT_BACKOFF_MAX_MS, delayMs);
}
function clearTransientCooldown() {
    transientCooldown = null;
    transientFailureCount = 0;
}
/** Testing hook — only for test setup/teardown. */
export function __resetInflightRefreshForTests() {
    inflight = null;
    clearTransientCooldown();
}
/** Testing hook — only for test setup/teardown. */
export function __resetRefreshGuardForTests() {
    resetRefreshBlock();
}
export function isRefreshInflight() {
    return inflight !== null;
}
//# sourceMappingURL=refresh-single-flight.js.map